import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import GanttChart from '../../Common/GanttChart/GanttChart';
import { DATE_FORMAT, EVENT_DATE_FORMAT, GANTT_LINK_TYPES, LIST_POLL_INTERVAL_MS } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Moment from 'moment';
import ChangeStartDateModal from '../../TaskList/Calendar/ChangeStartDateModal';
import ChangeDaysOutModal from './ChangeDaysOutModal';
import { useSearchBaselineTasksForGantt, useUpdateTaskDates, useUpdateContingent } from '../../../apis/baselineTasks';
import { GanttTask, BaselineTaskGanttSearch } from '../../../apis/baselineTasks/baselineTask.types';

interface GanttEventTask {
	id: number;
	text: string;
	eh_task: GanttTask;
	eh_showStatus: boolean;
	eh_noStartDate: boolean;
	duration: number;
	start_date: string;
}

interface GanttChartData {
	data: GanttEventTask[];
	links: { id: number; source: number; target: number; type: number }[];
	markers: unknown[];
}

interface GanttSearchParams {
	baselineId: string | number;
	name?: string;
	additionalInfo?: string;
	isActive?: boolean | null;
	departments: number[];
	baselineJobTitles: number[];
	communities: number[];
	phases: number[];
	disciplines: number[];
	createdDateStart?: Date | string | null;
	createdDateEnd?: Date | string | null;
}

interface GanttChartViewProps {
	subscriptionId: string;
	searchParams: GanttSearchParams;
	onSelectTask: (taskId: number) => void;
	lastFilterChange: Date;
	openingDate?: string | Date | null;
	zoomLevel: number;
	isTaskDependenciesActive: boolean;
	isStartDateActive: boolean;
}

const toIsoStringOrNull = (value?: Date | string | null): string | null => {
	if (!value) return null;
	return value instanceof Date ? value.toISOString() : value;
};

/**
 * The date callbacks feeding updateTaskDates are not uniform: GanttChart's resize handler and the
 * days-out modal hand back Moment objects, while ChangeStartDateModal (a legacy .js component with
 * no type information) hands back strings already formatted as DATE_FORMAT. Normalise both here.
 */
type TaskDateInput = Moment.Moment | string | null;

const taskDateToIso = (value: TaskDateInput): string | null => {
	if (!value) return null;
	if (Moment.isMoment(value)) return value.toISOString();
	return Moment.utc(value, DATE_FORMAT).toISOString();
};

const convertTasksToGanttData = (taskObjects: GanttTask[]): GanttChartData => {
	const retval: GanttChartData = { data: [], links: [], markers: [] };

	taskObjects.forEach((baselineTask) => {
		let eventText = baselineTask.name;
		if (baselineTask.isMilestone) {
			eventText = '&#x25C6; ' + baselineTask.name;
		}
		if (baselineTask.includeCertificationCheckbox) {
			eventText = '&#x2611; ' + eventText;
		}

		const eventTask: GanttEventTask = {
			id: baselineTask.id,
			text: eventText,
			eh_task: baselineTask,
			eh_showStatus: false,
			eh_noStartDate: false,
			duration: 1,
			start_date: '',
		};

		if (!baselineTask.startDate) {
			eventTask.eh_noStartDate = true;
			eventTask.duration = 1;
			eventTask.start_date = Moment.utc(baselineTask.targetDate).format(EVENT_DATE_FORMAT);
		} else {
			eventTask.eh_noStartDate = false;
			const startDate = Moment.utc(baselineTask.startDate);
			const targetDate = Moment.utc(baselineTask.targetDate);

			// add 1 to duration so the target date is included visually
			eventTask.duration = Moment.duration(targetDate.diff(startDate)).as('days') + 1;
			eventTask.start_date = startDate.format(EVENT_DATE_FORMAT);
		}

		// see if this task has any contingents and add a link
		const children = taskObjects.filter((m) => m.contingentId === baselineTask.id);
		children.forEach((child) => {
			retval.links.push({
				id: child.id,
				source: child.id,
				target: baselineTask.id,
				type: 0,
			});
		});

		retval.data.push(eventTask);
	});

	return retval;
};

const GanttChartView: React.FC<GanttChartViewProps> = ({
	searchParams,
	onSelectTask,
	openingDate,
	zoomLevel,
	isTaskDependenciesActive,
	isStartDateActive,
}) => {
	const [lastClickedEvent, setLastClickedEvent] = useState<GanttTask | null>(null);
	const [dateChangeDeltaDays, setDateChangeDeltaDays] = useState<number>(0);
	const [lastSearchTime, setLastSearchTime] = useState<Date>(new Date());
	const [showChangeStartDateModal, setShowChangeStartDateModal] = useState<boolean>(false);
	const [showChangeDaysOutModal, setShowChangeDaysOutModal] = useState<boolean>(false);
	const isLinkingRef = useRef<boolean>(false);
	const baselineTasksRef = useRef<GanttTask[]>([]);
	const openingDateRef = useRef<string>('');

	const effectiveOpeningDate = useMemo(() => {
		if (openingDate) {
			return openingDate instanceof Date ? openingDate.toISOString() : openingDate;
		}
		return Moment.utc(new Date()).toISOString();
	}, [openingDate]);

	useEffect(() => {
		openingDateRef.current = effectiveOpeningDate;
	}, [effectiveOpeningDate]);

	const ganttSearchRequest: BaselineTaskGanttSearch = useMemo(() => ({
		baselineId: String(searchParams.baselineId),
		openingDate: effectiveOpeningDate,
		name: searchParams.name || '',
		additionalInfo: searchParams.additionalInfo || '',
		isActive: searchParams.isActive ?? null,
		departments: searchParams.departments || [],
		baselineJobTitles: searchParams.baselineJobTitles || [],
		communities: searchParams.communities || [],
		phases: searchParams.phases || [],
		disciplines: searchParams.disciplines || [],
		// searchParams.tags is deliberately not forwarded - search-gantt ignores it (see the schema)
		createdDateStart: toIsoStringOrNull(searchParams.createdDateStart),
		createdDateEnd: toIsoStringOrNull(searchParams.createdDateEnd),
	}), [searchParams, effectiveOpeningDate]);

	const { data, isLoading: isLoadingGanttTasks, error } = useSearchBaselineTasksForGantt(ganttSearchRequest, {
		enabled: !!searchParams.baselineId,
		refetchInterval: LIST_POLL_INTERVAL_MS,
	});

	const isDateModalOpen = showChangeStartDateModal || showChangeDaysOutModal;
	const pendingReparseRef = useRef<boolean>(false);

	useEffect(() => {
		baselineTasksRef.current = data?.tasks ?? [];
		if (!data) {
			return;
		}
		if (isDateModalOpen) {
			// A 30s poll landing while a date modal is open would rebuild the chart underneath the
			// user (GanttChart re-parses on every lastSearchTime change). Defer until it closes.
			pendingReparseRef.current = true;
			return;
		}
		setLastSearchTime(new Date());
		// isDateModalOpen is read as a latch, not a trigger - the flush effect below handles closing
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	useEffect(() => {
		if (isDateModalOpen || !pendingReparseRef.current) {
			return;
		}
		pendingReparseRef.current = false;
		setLastSearchTime(new Date());
	}, [isDateModalOpen]);

	useEffect(() => {
		if (error) {
			Notification('error', 'An Error Occurred', error.message);
		}
	}, [error]);

	useEffect(() => {
		if (data?.isError) {
			Notification('error', 'An Error Occurred', data.errors?.[0]?.message ?? 'Failed to load Gantt tasks');
		}
	}, [data]);

	const droppedTaskCount = data?.droppedTaskCount ?? 0;

	useEffect(() => {
		if (!droppedTaskCount) {
			return;
		}
		// A dropped row also removes any dependency link drawn to it, so say so rather than
		// letting the chart quietly render an incomplete picture. Keyed on the count, not on data
		// identity: the 30s poll would otherwise re-toast for the same malformed row every cycle.
		Notification(
			'error',
			'Some Tasks Could Not Be Displayed',
			`${droppedTaskCount} task${droppedTaskCount === 1 ? '' : 's'} returned unexpected data and ${droppedTaskCount === 1 ? 'was' : 'were'} left off the chart.`
		);
	}, [droppedTaskCount]);

	const chartData = useMemo(() => convertTasksToGanttData(data?.tasks ?? []), [data?.tasks]);

	const undoChanges = useCallback(() => {
		// resets the gantt chart without having to hit the api
		isLinkingRef.current = false;
		setLastSearchTime(new Date());
	}, []);

	const updateTaskDatesMutation = useUpdateTaskDates((result) => {
		if (result.isError) {
			Notification('error', 'Error', result.errors?.[0]?.message ?? 'Failed to update task');
			setLastSearchTime(new Date());
		} else {
			Notification('success', '', 'Task updated');
		}
	});

	useEffect(() => {
		if (updateTaskDatesMutation.error) {
			Notification('error', 'Error', updateTaskDatesMutation.error.message);
			setLastSearchTime(new Date());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateTaskDatesMutation.error]);

	const updateTaskDates = useCallback((taskId: number, startDate: TaskDateInput, targetDate: TaskDateInput) => {
		updateTaskDatesMutation.mutate({
			taskId,
			startDate: taskDateToIso(startDate),
			targetDate: taskDateToIso(targetDate) ?? '',
			fakeOpeningDate: openingDateRef.current,
		});
		// mutate is referentially stable (react-query guarantee)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateContingentMutation = useUpdateContingent((result) => {
		isLinkingRef.current = false;
		if (result.isError) {
			Notification('error', 'Error', result.errors?.[0]?.message ?? 'Failed to update task');
			// The rejected link is still drawn on the chart and the refetched payload is identical,
			// so structural sharing means no data change would ever trigger a re-parse. Force one.
			undoChanges();
		} else {
			Notification('success', '', 'Task updated');
		}
	});

	useEffect(() => {
		if (updateContingentMutation.error) {
			Notification('error', 'Error', updateContingentMutation.error.message);
			undoChanges();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateContingentMutation.error]);

	// ---- Stable handlers bound once by the underlying gantt library ----
	// GanttChart attaches these on mount and never re-binds them, so they must
	// read fresh state via refs rather than closing over props/state directly.

	const onMoveTask = useCallback((taskId: number, deltaDays: number) => {
		const tasks = baselineTasksRef.current;
		const movingTask = tasks.find((m) => m.id === taskId);
		if (!movingTask) {
			// the poll can drop a task between render and drop; put the bar back where it was
			undoChanges();
			return;
		}

		if (movingTask.contingentId) {
			// pre-req is moving
			const movingTaskEndDate = Moment(movingTask.targetDate).add(deltaDays, 'days');
			const successorTask = tasks.find((m) => m.id === movingTask.contingentId);

			if (!successorTask) {
				Notification('error', '', 'Child task not found.');
				undoChanges();
				return;
			}

			const childStartDate = successorTask.startDate ? Moment(successorTask.startDate) : Moment(successorTask.targetDate);
			const diffDays = Moment.duration(movingTaskEndDate.diff(childStartDate)).as('days');

			if (diffDays > 1) {
				Notification('error', '', 'A child task cannot start or end before the start of its contingent');
				undoChanges();
				return;
			}
		} else {
			// successorTask is moving
			const prereqTask = tasks.find((m) => m.contingentId === movingTask.id);

			if (prereqTask) {
				const parentTargetDate = Moment(prereqTask.targetDate);
				const movingTaskStartDate = (movingTask.startDate ? Moment(movingTask.startDate) : Moment(movingTask.targetDate)).add(deltaDays, 'days');
				const diffDays = Moment.duration(movingTaskStartDate.diff(parentTargetDate)).as('days');

				if (diffDays <= 0) {
					Notification('error', '', 'A Child task cannot start or end before the end of its contingent');
					undoChanges();
					return;
				}
			}
		}

		setLastClickedEvent(movingTask);
		setDateChangeDeltaDays(deltaDays);
		if (movingTask.targetStaticDate) {
			setShowChangeStartDateModal(true);
		} else {
			setShowChangeDaysOutModal(true);
		}
		// undoChanges is stable ([] deps); state setters are stable
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onResizeTask = useCallback((taskId: number, originalStart: Moment.Moment | null, originalTarget: Moment.Moment, updatedStart: Moment.Moment, updatedTarget: Moment.Moment) => {
		const tasks = baselineTasksRef.current;
		const movingTask = tasks.find((m) => m.id === taskId);
		if (!movingTask) {
			// the poll can drop a task between render and resize; put the bar back where it was
			undoChanges();
			return;
		}

		if (movingTask.contingentId) {
			// pre-req is moving
			const childTask = tasks.find((m) => m.id === movingTask.contingentId);

			if (!childTask) {
				Notification('error', '', 'Child task not found.');
				undoChanges();
				return;
			}

			const childTaskStartDate = childTask.startDate ? Moment(childTask.startDate) : Moment(childTask.targetDate);
			const diffDays = Moment.duration(updatedTarget.diff(childTaskStartDate)).as('days');

			if (diffDays >= 1) {
				Notification('error', '', 'A child task cannot start or end before the start of its parent');
				undoChanges();
				return;
			}
		} else {
			// successorTask is moving
			const prereqTask = tasks.find((m) => m.contingentId === movingTask.id);
			if (prereqTask) {
				const parentTargetDate = Moment(prereqTask.targetDate);
				const diffDays = Moment.duration(updatedStart.diff(parentTargetDate)).as('days');

				if (diffDays < 0) {
					Notification('error', '', 'A child task cannot start or end before the start of its parent');
					undoChanges();
					return;
				}
			}
		}

		// figure out which end was resized - only one side can change at a time
		// != does not work so check duration diff
		const startDiffBase = originalStart ?? originalTarget;
		if (Moment.duration(updatedStart.diff(startDiffBase)).as('days') !== 0) updateTaskDates(taskId, updatedStart, originalTarget);
		else if (Moment.duration(updatedTarget.diff(originalTarget)).as('days') !== 0) updateTaskDates(taskId, originalStart, updatedTarget);

		// undoChanges/updateTaskDates are stable ([] deps)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onRemoveLink = useCallback((taskId: string | number) => {
		updateContingentMutation.mutate({ taskId: Number(taskId), contingentId: null });
		// mutate is referentially stable (react-query guarantee)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onAddLink = useCallback((sourceId: string | number, targetId: string | number, type: string) => {
		// preventive measure to stop dupes - stupid thing fires like 4-5 times
		if (isLinkingRef.current) return;

		const tasks = baselineTasksRef.current;
		const sourceTask = tasks.find((m) => m.id === parseInt(String(sourceId), 10));
		const targetTask = tasks.find((m) => m.id === parseInt(String(targetId), 10));
		if (!sourceTask || !targetTask) {
			// the poll can drop either end between render and drop; remove the drawn link
			undoChanges();
			return;
		}

		isLinkingRef.current = true;

		let newContingentTaskId: number;
		let newContingentId: number;

		if (type === GANTT_LINK_TYPES.StartToStart || type === GANTT_LINK_TYPES.FinishToStart) {
			// A task can have multiple pre-req tasks; but a task can only be a pre-req for one task.
			if (sourceTask.contingentId) {
				Notification('error', '', 'A task can only be a prereq of 1 task');
				undoChanges();
				return;
			}

			const parentTargetDate = Moment(sourceTask.targetDate);
			const childTargetDate = Moment(targetTask.targetDate);
			const diffDays = Moment.duration(parentTargetDate.diff(childTargetDate)).as('days');

			if (diffDays > 0) {
				Notification('error', '', 'A contingent task cannot start or end before the start of the other task');
				undoChanges();
				return;
			}

			newContingentTaskId = sourceTask.id;
			newContingentId = targetTask.id;
		} else {
			// StartToFinish or FinishToFinish
			if (targetTask.contingentId) {
				Notification('error', '', 'A task can only be a prereq of 1 task');
				undoChanges();
				return;
			}

			const parentTargetDate = Moment(targetTask.targetDate);
			const childTargetDate = Moment(sourceTask.targetDate);
			const diffDays = Moment.duration(childTargetDate.diff(parentTargetDate)).as('days');

			if (diffDays < 0) {
				Notification('error', '', 'A contingent task cannot start or end before the start of the other task');
				undoChanges();
				return;
			}

			newContingentTaskId = targetTask.id;
			newContingentId = sourceTask.id;
		}

		updateContingentMutation.mutate({ taskId: newContingentTaskId, contingentId: newContingentId });
		// undoChanges is stable; mutate is referentially stable (react-query guarantee)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onCloseChangeStartDateModal = useCallback(() => {
		undoChanges();
		setShowChangeStartDateModal(false);
	}, [undoChanges]);

	// ChangeStartDateModal reports both dates as DATE_FORMAT strings, not Moments
	const onCompleteChangeStartDateModal = useCallback((startDate: string, targetDate: string) => {
		setShowChangeStartDateModal(false);
		if (lastClickedEvent) {
			updateTaskDates(lastClickedEvent.id, startDate, targetDate);
		}
	}, [lastClickedEvent, updateTaskDates]);

	const onCompleteChangeDaysOutModal = useCallback(() => {
		setShowChangeDaysOutModal(false);
		if (lastClickedEvent) {
			const newStartDate = Moment(lastClickedEvent.startDate).add(dateChangeDeltaDays, 'days');
			const newTargetDate = Moment(lastClickedEvent.targetDate).add(dateChangeDeltaDays, 'days');
			updateTaskDates(lastClickedEvent.id, newStartDate, newTargetDate);
		}
	}, [lastClickedEvent, dateChangeDeltaDays, updateTaskDates]);

	const onCloseChangeDaysOutModal = useCallback(() => {
		undoChanges();
		setShowChangeDaysOutModal(false);
	}, [undoChanges]);

	const handleSelectTask = useCallback((taskId: number) => {
		onSelectTask(taskId);
		// onSelectTask is bound once by the gantt library at mount; kept in deps for lint parity only
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getHeight = () => window.innerHeight - 175;

	return (
		<div className="gantt-chart-view">
			<LoadingIndicator isLoading={isLoadingGanttTasks}>
				<GanttChart
					chartData={chartData}
					height={getHeight()}
					onMoveTask={onMoveTask}
					onResizeTask={onResizeTask}
					onRemoveLink={onRemoveLink}
					onAddLink={onAddLink}
					onSelectTask={handleSelectTask}
					lastSearchTime={lastSearchTime}
					zoomLevel={zoomLevel}
					exportToPDF={false}
					startOnMonday={false}
					showCriticalPath={false}
					isTaskDependenciesActive={isTaskDependenciesActive}
					isStartDateActive={isStartDateActive}
				/>
			</LoadingIndicator>
			{showChangeStartDateModal && lastClickedEvent && (
				<ChangeStartDateModal
					task={lastClickedEvent}
					onCancel={onCloseChangeStartDateModal}
					onComplete={onCompleteChangeStartDateModal}
					deltaDays={dateChangeDeltaDays}
					userCanChangeTargetDate={true}
				/>
			)}
			{showChangeDaysOutModal && lastClickedEvent && (
				<ChangeDaysOutModal
					task={{ targetDaysOut: lastClickedEvent.targetDaysOut ?? 0, startDaysOut: lastClickedEvent.startDaysOut }}
					deltaDays={dateChangeDeltaDays}
					onComplete={onCompleteChangeDaysOutModal}
					onCancel={onCloseChangeDaysOutModal}
				/>
			)}
		</div>
	);
};

export default GanttChartView;
